/**
 * P0-DOB-ONBOARDING-01 — Clinical profile → Personal information.
 *
 * A real personal-information form, not a notice. It projects the
 * EXISTING canonical identity record (`GET /api/patient/profile/personal`,
 * read-only) and lets the patient supply the one field that is genuinely
 * theirs to supply: a missing date of birth, written through the single
 * canonical writer.
 *
 * Fields the platform has no safe patient-facing update path for (legal
 * name, verified e-mail, verified phone) are shown read-only with the
 * route to change them — never through an unverified shortcut.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { formatDateLocale } from '../../utils/dateFormat';
import { DateOfBirthGate } from './DateOfBirthGate';
import { Lock, UserRound } from 'lucide-react';

const ReadOnlyRow = ({ label, value, testId, note }) => {
  const { t } = useTranslation();
  return (
    <div data-testid={testId}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 flex items-center gap-1.5 text-[13.5px] text-slate-900">
        {value || <span className="italic text-slate-400">{t('patient.personal.notProvided')}</span>}
        {value && <Lock className="h-3 w-3 shrink-0 text-slate-300" aria-hidden="true" />}
      </p>
      {note && <p className="mt-0.5 text-[11px] text-slate-400">{note}</p>}
    </div>
  );
};

export const PersonalInformationCard = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  // No `t` in the dependency list: `useTranslation()` returns a fresh `t`
  // on every render, which would make `load` unstable and re-fire the effect
  // on every render (an unbounded fetch/render loop).
  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await api.get('/patient/profile/personal');
      setData(res.data);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const personal = data?.personal || {};
  const dobEditable = data?.editable?.date_of_birth;
  const eligibility = data?.eligibility || {};

  return (
    <section
      className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-3"
      data-testid="cp-section-personal-information"
    >
      <div className="mb-3 flex items-start gap-2">
        <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
        <div>
          <h2 className="text-sm font-semibold text-slate-900">
            {t('patient.dob.personalInformation')}
          </h2>
          <p className="text-[12px] text-slate-500">{t('patient.personal.sectionSub')}</p>
        </div>
      </div>

      {loading && (
        <div className="text-[12px] text-slate-500" data-testid="cp-personal-loading">
          {t('patient.common.loading')}
        </div>
      )}
      {failed && (
        <div className="text-[12px] text-rose-600" data-testid="cp-personal-error">
          {t('patient.personal.loadFailed')}
        </div>
      )}

      {!loading && !failed && (
        <>
          <div className="grid gap-4 sm:grid-cols-2" data-testid="cp-personal-grid">
            <ReadOnlyRow
              label={t('patient.personal.firstName')}
              value={personal.first_name}
              testId="cp-personal-first-name"
            />
            <ReadOnlyRow
              label={t('patient.personal.lastName')}
              value={personal.last_name}
              testId="cp-personal-last-name"
            />
            <ReadOnlyRow
              label={t('patient.personal.email')}
              value={personal.email}
              testId="cp-personal-email"
              note={personal.email ? t('patient.personal.verifiedNote') : undefined}
            />
            <ReadOnlyRow
              label={t('patient.personal.phone')}
              value={personal.phone}
              testId="cp-personal-phone"
              note={personal.phone ? t('patient.personal.verifiedNote') : undefined}
            />
            {personal.residence_country && (
              <ReadOnlyRow
                label={t('patient.personal.residenceCountry')}
                value={personal.residence_country}
                testId="cp-personal-residence-country"
              />
            )}
          </div>

          <div className="mt-4 border-t border-slate-100 pt-4">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              {t('patient.dob.label')}
            </p>
            {personal.date_of_birth && !dobEditable ? (
              <>
                <p className="mt-0.5 text-[13.5px] font-medium text-slate-900" data-testid="cp-dob-value">
                  {formatDateLocale(personal.date_of_birth, i18n.language)}
                </p>
                <p className="mt-0.5 text-[11px] text-slate-400" data-testid="cp-dob-on-file">
                  {t('patient.personal.dobLockedNote')}
                </p>
              </>
            ) : (
              <div className="mt-2">
                <DateOfBirthGate
                  code={eligibility.code}
                  message={eligibility.message || t('patient.personal.dobMissing')}
                  testIdPrefix="cp-dob"
                  onSaved={() => load()}
                />
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default PersonalInformationCard;
